import React, { useEffect, useState, useMemo } from 'react';
import GeneriqueDataForm from '../components/Forms/GeneriqueDataForm';
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, RootState } from '../reduxStore/Store';
import { fetchGeneriqueDataByType, setSelectedGeneriqueData } from '../reduxStore/GeneriqueDataSlice';
import { fetchSysGeneriqueDataTypes } from '../reduxStore/SysGeneriqueDataTypeSlice';
import {
  fetchEntitiesByCategorie,
  clearCategoryEntities,
  setSelectedFamille,
} from '../reduxStore/FamilleSlice';
import MyTable from '../components/MyTable';
import { TableHeader, TableAction } from '../constants/interfaces';
import { GeneriqueData, FamilleData } from '../models/models';
import { ButtonColor, ButtonType, GeneriqueDataType, PorteeGenerique, leafTypeOfCategory } from '../constants/enums';
import { Box, CircularProgress, DialogTitle, Divider, MenuItem, TextField, Typography } from '@mui/material';
import { useDialog } from '../components/DialogComponent';
import CategoryIcon from '@mui/icons-material/Category';
import AppPage from '../components/layout/AppPage';
import AppSurface from '../components/layout/AppSurface';
import AppPageHeader from '../components/layout/AppPageHeader';
import FamilleHierarchie from './FamilleHierarchie';

// donnees_supplementaires arrive en chaîne JSON (LONGTEXT) — parse défensif.
const parseDonnees = (raw: any): Record<string, any> => {
    if (!raw) return {};
    if (typeof raw === 'object') return raw;
    try { return JSON.parse(raw); } catch { return {}; }
};
const IMPACT_LABELS: Record<string, string> = { PRODUIT: 'Produit', CHARGE: 'Charge', NEUTRE: 'Neutre' };

const GestionGeneriqueData: React.FC = () => {
     const dispatch = useDispatch<AppDispatch>();
     const { byType, selectedGeneriqueData } = useSelector((state: RootState) => state.GeneriqueDataSlice);
     const { types: generiqueTypes } = useSelector((state: any) => state.SysGeneriqueDataTypeSlice);
     const { selectedFamille, categoryEntities, categoryEntitiesLoading } =
       useSelector((state: any) => state.FamilleSlice);
     const isAllMode = useSelector((state: RootState) => state.AppSlice.isAllMode);
     // Mono-département : un seul dept autorisé (tenant à un seul dept, ou user
     // restreint à un seul) → "ce département" et "Tous les départements"
     // représentent exactement les mêmes données (même règle que
     // AllModeRequiredRoute/useDepartementBootstrap). Sans ça, un tel utilisateur
     // n'entre jamais en isAllMode (auto-sélection saute le picker) et perd tout
     // accès aux types GLOBAL (familles, règlements, natures de tâche...).
     const allowedDepartements = useSelector((state: RootState) => state.ScopeSlice.allowedDepartements);
     const isMonoDepartement = allowedDepartements.length === 1;

     // Cohérence avec la portée : en mode "tous départements" on ne gère que les
     // paramètres GLOBAL (communs) ; positionné sur un département, uniquement les
     // paramètres DEPARTEMENT (CAISSE, DEPOT). En mono-département, les deux
     // portées coexistent (cf. commentaire ci-dessus). Le sélecteur de type est
     // filtré en conséquence (useMemo → référence stable pour l'effet de sélection
     // ci-dessous).
     const visibleTypes = useMemo(() => {
       if (isMonoDepartement) return generiqueTypes;
       return generiqueTypes.filter((t: any) =>
         isAllMode ? t.portee === PorteeGenerique.GLOBAL : t.portee === PorteeGenerique.DEPARTEMENT);
     }, [generiqueTypes, isAllMode, isMonoDepartement]);

     const [selectedType, setSelectedType] = useState<GeneriqueDataType>(GeneriqueDataType.CAISSE);
     const [hierarchieKey, setHierarchieKey] = useState(0);

     // Données du tableau : on lit le slot DÉDIÉ au type courant (byType[selectedType]),
     // et non le champ partagé `generiqueData`. Sinon un SelectCategorie ouvert dans
     // le formulaire (qui fetch un AUTRE type, ex: CATEGORIE_NATURE_REGLEMENT) écrase
     // `generiqueData` et fait disparaître les natures du tableau en arrière-plan.
     const generiqueData = byType[selectedType] ?? [];

     // Si le type sélectionné est un type "catégorie", on affiche en plus la
     // gestion hiérarchique (arbre + entités rattachées). leafType = type "feuille"
     // catégorisé par ce type catégorie (ex: NATURE_REGLEMENT pour CATEGORIE_NATURE_REGLEMENT).
     const leafType = leafTypeOfCategory(selectedType);
     const isCategoryType = !!leafType;

     const [FormDialog, openDialog, closeDialog] = useDialog({
       dialogProps: { maxWidth: 'sm', fullWidth: true }
     });

     useEffect(() => {
       dispatch(fetchSysGeneriqueDataTypes());
     }, []);

     // Dès que les types arrivent ou que le mode change : si le type courant n'est
     // plus proposé (mauvaise portée pour le mode), basculer sur le 1er type valide.
     // C'est aussi cet effet qui déclenche le chargement initial des données.
     useEffect(() => {
       if (visibleTypes.length === 0) return;
       const valid = visibleTypes.some((t: any) => t.code === selectedType);
       const next = valid ? selectedType : (visibleTypes[0].code as GeneriqueDataType);
       if (next !== selectedType) setSelectedType(next);
       dispatch(fetchGeneriqueDataByType({ type: next }));
       // eslint-disable-next-line react-hooks/exhaustive-deps
     }, [visibleTypes]);

     // Entités rattachées à la catégorie sélectionnée dans l'arbre.
     useEffect(() => {
       if (leafType && selectedFamille) {
         dispatch(fetchEntitiesByCategorie({ leafType, categorie_id: selectedFamille.id }));
       } else {
         dispatch(clearCategoryEntities());
       }
     }, [dispatch, leafType, selectedFamille]);

     // Nettoyage du state hiérarchie partagé au démontage de la page.
     useEffect(() => {
       return () => {
         dispatch(setSelectedFamille(null));
         dispatch(clearCategoryEntities());
       };
     }, [dispatch]);

     const handleTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
       const type = e.target.value as GeneriqueDataType;
       setSelectedType(type);
       dispatch(fetchGeneriqueDataByType({ type }));
     };

     const handleCancel = () => {
       closeDialog();
       dispatch(setSelectedGeneriqueData(null));
     };

     // Création/modification réussie : recharger le tableau et, pour un type
     // catégorie, remonter l'arbre pour qu'il intègre la nouvelle entité.
     const handleSuccess = () => {
       dispatch(fetchGeneriqueDataByType({ type: selectedType }));
       setHierarchieKey((k) => k + 1);
     };

     const headers: TableHeader<GeneriqueData>[] = [
       { key: 'nom', label: 'Nom' },
       // Colonne « Impact résultat » : uniquement pour les catégories de nature de règlement.
       ...(selectedType === GeneriqueDataType.CATEGORIE_NATURE_REGLEMENT ? [{
         key: 'donnees_supplementaires' as keyof GeneriqueData,
         label: 'Impact résultat',
         render: (row: GeneriqueData) =>
           IMPACT_LABELS[parseDonnees(row.donnees_supplementaires).impact_resultat] ?? '—',
       }] : []),
       {
         key: 'visible',
         label: 'Visible',
         render: (row: GeneriqueData) => (
           <input
             type="checkbox"
             checked={row.visible || false}
             disabled
             style={{ cursor: 'default' }}
           />
         )
       }
     ];

     const actions: TableAction<GeneriqueData>[] = [
       {
         label: 'Modifier',
         callback: (row: GeneriqueData) => {
           dispatch(setSelectedGeneriqueData(row));
           openDialog();
         },
         color: ButtonColor.PRIMARY,
         type: ButtonType.EDIT
       },
     ];

    return (
        <AppPage maxWidth={false}>
            <AppPageHeader
                title="Données génériques"
                icon={<CategoryIcon />}
                addLabel="Ajouter"
                onAdd={openDialog}
                actions={
                    <TextField
                        select
                        label="Type de Donnée"
                        value={selectedType}
                        onChange={handleTypeChange}
                        size="small"
                        sx={{ minWidth: 200 }}
                    >
                        {visibleTypes.map((type: any) => (
                            <MenuItem key={type.code} value={type.code}>
                                {type.libelle}
                            </MenuItem>
                        ))}
                    </TextField>
                }
            />

            <AppSurface>
                <MyTable
                    headers={headers}
                    data={generiqueData}
                    actions={actions}
                    totalRows={generiqueData.length}
                    defaultPageSize={5}
                />
            </AppSurface>

            {/* Type "catégorie" : gestion hiérarchique (imbrication + entités rattachées) */}
            {isCategoryType && (
                <>
                    <AppSurface sx={{ mt: 2 }}>
                        <FamilleHierarchie
                            key={`${selectedType}-${hierarchieKey}`}
                            type={selectedType}
                            entityLabel="catégorie"
                        />
                    </AppSurface>

                    {selectedFamille && (
                        <AppSurface sx={{ mt: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                Éléments rattachés à « {selectedFamille.nom} »
                            </Typography>
                            <Divider sx={{ mb: 1.5 }} />
                            {categoryEntitiesLoading ? (
                                <Box display="flex" justifyContent="center" py={2}>
                                    <CircularProgress size={22} />
                                </Box>
                            ) : categoryEntities.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    Aucun élément rattaché à cette catégorie.
                                </Typography>
                            ) : (
                                categoryEntities.map((entity: FamilleData) => (
                                    <Typography key={entity.id} variant="body2" sx={{ py: 0.3 }}>
                                        • {entity.nom}
                                    </Typography>
                                ))
                            )}
                        </AppSurface>
                    )}
                </>
            )}

            <FormDialog>
                <DialogTitle>
                    <Typography variant="h6">
                        {selectedGeneriqueData ? 'Modifier' : 'Ajouter'}
                    </Typography>
                </DialogTitle>
                <GeneriqueDataForm
                    onCancel={handleCancel}
                    typeDonnee={selectedType}
                    onSuccess={handleSuccess}
                />
            </FormDialog>
        </AppPage>
    );
};

export default GestionGeneriqueData;
